import React from "react";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { usePriceRules, useRemovePriceRule } from "../hooks";
import { useProducts } from "../../products/hooks";
import { useCustomers } from "../../customers/hooks";
import { useMe } from "../../users/hooks/useMe";
import { UserRole } from "../../../__generated__/globalTypes";

interface IProps {
  productId?: number;
  customerId?: number;
}

export const PriceRulesList: React.FC<IProps> = ({ productId, customerId }) => {
  const { data, loading, refetch } = usePriceRules({ where: { productId, customerId } });
  const { data: productsData } = useProducts({ where: {}, limit: 100, offset: 0 });
  const { data: customersData } = useCustomers({ where: {} });
  const { data: meData } = useMe();
  const [remove] = useRemovePriceRule();

  const isAdmin = meData?.me?.role === UserRole.Admin;

  const handleRemove = async (id: number) => {
    if (!window.confirm("Supprimer cette règle de prix ? Cette action est irréversible.")) return;
    await remove({ variables: { id } });
    refetch();
  };

  if (loading) return <Loading />;

  if (!data?.priceRules?.results?.length) {
    return <EmptyList text="Aucune règle de prix" />;
  }

  const productName = (id: number | null) =>
    productsData?.products?.results?.find((product) => product.id === id)?.name ?? `#${id}`;
  const customerName = (id: number | null) =>
    customersData?.customers?.results?.find((customer) => customer.id === id)?.name ?? `#${id}`;

  return (
    <Table>
      <TableHeader>
        <TableRow hover={false}>
          {!productId && <TableCell header>Produit</TableCell>}
          {!customerId && <TableCell header>Client</TableCell>}
          <TableCell header align="right">
            Quantité min.
          </TableCell>
          <TableCell header align="right">
            Prix
          </TableCell>
          {isAdmin && <TableCell header align="center">Actions</TableCell>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data?.priceRules?.results?.map((priceRule) => (
          <TableRow key={`price-rule-${priceRule.id}`} hover={false}>
            {!productId && <TableCell>{productName(priceRule.productId)}</TableCell>}
            {!customerId && (
              <TableCell>
                {priceRule.customerId ? customerName(priceRule.customerId) : "Tous"}
              </TableCell>
            )}
            <TableCell align="right">{priceRule.quantity ?? 1}</TableCell>
            <TableCell align="right">
              {((priceRule.priceCents ?? 0) / 100).toFixed(2)} €
            </TableCell>
            {isAdmin && (
              <TableCell align="center">
                <span
                  className="text-red-600 text-sm cursor-pointer hover:underline"
                  onClick={() => handleRemove(priceRule.id)}
                >
                  Supprimer
                </span>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
